import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Wallet, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/i18n";
import api from "@/api";

export default function Jobs() {
  const { t, catName, money, jt, cityName } = useI18n();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [list, setList] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (category && category !== "all") p.category = category;
    api.get("/jobs", { params: p }).then((r) => setList(r.data));
    setParams(new URLSearchParams(p), { replace: true });
    // eslint-disable-next-line
  }, [q, category]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-[#2D231F]">{t("jobs.title")}</h1>
      <p className="text-[#8C7A70] mt-2 mb-8">{t("jobs.subtitle")}</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-10">
        <div className="flex-1 flex items-center gap-2 bg-white rounded-xl border border-[#EBDDD3] px-3">
          <Search className="w-4 h-4 text-[#A89A90]" />
          <Input data-testid="jobs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hero.searchPlaceholder")} className="border-0 shadow-none focus-visible:ring-0 px-0" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="jobs-category" className="sm:w-56 rounded-xl bg-white h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("jobs.allCategories")}</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{catName(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {list.length === 0 ? (
        <p className="text-[#A89A90] py-20 text-center">{t("jobs.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {list.map((j, i) => (
            <motion.div key={j._id} className="h-full" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link to={`/jobs/${j._id}`} data-testid={`job-card-${j._id}`}
                className="flex flex-col h-full min-h-[200px] rounded-xl border border-[#EBDDD3] bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-bold text-lg text-[#2D231F] line-clamp-2">{jt(j, "title")}</h3>
                  <span className="shrink-0 px-2.5 py-1 rounded-full bg-[#5C6646]/10 text-[#5C6646] font-semibold text-xs">{catName(j.category)}</span>
                </div>
                <p className="text-sm text-[#8C7A70] mt-2 line-clamp-2 min-h-[2.5rem]">{jt(j, "description")}</p>
                <div className="flex items-center gap-4 mt-auto pt-4 text-sm text-[#8C7A70] flex-wrap">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{cityName(j.city)}</span>
                  {j.budget != null && <span className="inline-flex items-center gap-1 font-semibold text-[#5A4B45]"><Wallet className="w-4 h-4" />{money(j.budget)}</span>}
                  <span className="inline-flex items-center gap-1"><Users className="w-4 h-4" />{j.applications} {j.applications === 1 ? t("jobs.application") : t("jobs.applications")}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}